import React, { useState, useCallback, useContext, useRef, useImperativeHandle } from 'react'
import classnames from 'clsx'
import styled, { css } from 'styled-components'
import { rem } from 'polished'

import zIndex from 'styles/tools/z-index'
import { mediaQueryUp } from 'components/utils/breakpoint'
import AutoTooltipContent from 'components/Common/AutoTooltip/AutoTooltipContent'
import uuidV4 from 'lib/uuidV4'
import { trackAutosuggestionClick, trackAutosuggestionsResult } from 'api/interactionStudio'
import GeoContext from 'contexts/geoContext'
import SelectPlacesAndProperties from './SelectPlacesAndProperties'

import ValueContainer from './ReactSelectComponents/ReactSelectValueContainer'
import ClearIndicator from './ReactSelectComponents/ReactSelectClearIndicator'
import LoadingMessage from './ReactSelectComponents/ReactSelectLoadingMessage'
import Input from './ReactSelectComponents/ReactSelectInput'
import Option from './ReactSelectComponents/ReactSelectOption'
import Control from './ReactSelectComponents/ReactSelectControl'
import NoOptionsMessage from './ReactSelectComponents/ReactSelectNoOptionsMessage'
import SingleValue from './ReactSelectComponents/ReactSelectSingleValue'
import noop from 'lib/noop'
import { LOCATION_SEARCH_ERROR_MESSAGE, LOCATION_SEARCH_ERROR_TOOLTIP_MESSAGE, LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL, LOCATION_SEARCH_INPUT_TITLE_LABEL } from 'constants/search'
import { TypeAheadSearchTypes } from 'api/search'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'

const selectStyle = css`
  .select-dropdown__control {
    align-items: center;
    border-radius: ${props => props.theme.borderRadius.small};
    cursor: pointer;
    transition: background-color 0.2s;
    box-shadow: none;
    border-color: ${({ theme }) => theme.palette.neutral.five};
    height: ${rem(75)}; /* set select-dropdown__control height of 60px on mobile view */

    .has-error & {
      border-color: ${({ theme }) => theme.palette.messaging.critical.main};
    }

    &:hover {
      border-color: ${({ theme }) => theme.palette.neutral.five};
    }

    .select-dropdown__input {
      top: ${rem(24)};
      ${mediaQueryUp.sm} {
        top: ${rem(22)};
      }
    }
    ${mediaQueryUp.sm} {
      height: ${rem(50)};
      .highlight-border {
        display: none; /* hide the highlighter for the input in desktop view */
      }
    }
  }
  .select-dropdown__control--is-focused {
    .select-dropdown__input {
      top: ${rem(24)};
      ${mediaQueryUp.sm} {
        top: ${rem(12)};
      }
    }
    .select-dropdown__single-value,
    .react-select-label,
    .select-dropdown__placeholder {
      display: none;
    }
  }
  .select-dropdown__control--menu-is-open + .select-dropdown__menu {
    margin-top: ${rem(16)};

    ${mediaQueryUp.sm} {
      margin-top: 0;
    }
  }
  .select-dropdown__indicator-separator {
    display: none;
  }
  .select-dropdown__indicators {
    position: absolute;
    top: 50%;
    right: ${rem(20)};
    transform: translate(0, -50%);
    z-index: ${zIndex.balloonArrow};
    display: none;
  }
  .select-dropdown__indicator {
    color: ${({ theme }) => theme.palette.neutral.one};
  }
  .select-dropdown__single-value {
    font-weight: ${props => props.theme.font.primary.weight.regular};
    font-size: ${({ theme }) => theme.fontSize.sm};
    color: ${props => props.theme.palette.neutral.one};
    transform: translateY(-50%);
    display:flex;
    align-items:center;
    width: calc(100% - ${rem(52)});
    padding-left: ${rem(72)};
    position: absolute;
    height: ${rem(20)};
    margin: 0;
    .add-button {
      display: none;
    }
    ${mediaQueryUp.sm} {
      padding-left: ${rem(16)};
      top: ${rem(32)};
    }
  }
  .select-dropdown__value-container {
    padding: 0;
    height: 100%;
    align-self:stretch;
    z-index:${zIndex.balloon};
    & div {
      align-self:stretch;
    }
  }
  .select-dropdown__placeholder {
    font-weight: ${props => props.theme.font.primary.weight.regular};
    color: ${({ theme }) => theme.palette.neutral.three};
    font-size: ${({ theme }) => theme.fontSize.sm};
    width:100%;
    display:flex;
    align-items: center;
    margin-left: ${rem(72)};
    position: relative;
    height: ${rem(20)};
    ${mediaQueryUp.sm} {
      margin-left: ${rem(16)};
      top: ${rem(10)};
    }
  }
  .select-dropdown__menu {
    box-shadow: ${({ theme }) => theme.shadow.flat.small};
    margin-bottom: 0;
    margin-top: 0;
    border: none;
    z-index: ${zIndex.searchLocationSuggestions};
    width: 100%;
    top: calc(100% + ${rem(8)});
    overflow: hidden;
    border-radius: ${props => props.theme.borderRadius.medium};
    padding: ${rem(24)};

    ${mediaQueryUp.sm} {
      padding: ${rem(24)} ${rem(20)};
    }
  }
  .select-dropdown__option {
    padding: 0;
    background-color: ${props => props.theme.palette.neutral.eight};
    color: inherit;
    cursor: pointer;
  }
  .select-dropdown__menu-list {
    padding-top: 0;
    padding-bottom: 0;
    max-height: none;
  }
  .select-dropdown__clear-indicator {
    padding-left: 0;
    padding-right: ${rem(16)};
    svg {
      width: ${rem(24)};
      height: ${rem(24)};
      color: ${({ theme }) => theme.palette.neutral.two};
    }
  }
  .select-dropdown__menu-notice--no-options {
    color: unset;
    padding: 0;
    text-align: unset;
  }
  .select-dropdown__input {
    position: absolute;
    top: ${rem(14)};
    bottom: 0;
    & input {
      padding-left: ${rem(72)};
      padding-right: ${rem(16)};
      height: ${rem(20)};
      ${mediaQueryUp.sm} {
        padding-left: ${rem(16)};
      }
    }
  }
`

const StyledLocationInput = styled(SelectPlacesAndProperties)<
  React.ComponentProps<typeof SelectPlacesAndProperties>
>`
  ${selectStyle}
`

const Container = styled.div`
  height: auto;
  position: relative;

  .select-dropdon__value-container {
    margin-left: ${rem(3)};
  }
  ${mediaQueryUp.sm} {
    display: block;
    flex: 42;
    height: ${rem(48)};
    .select-dropdon__value-container {
      margin-left: ${rem(15)};
    }
  }
  ${mediaQueryUp.md} {
    flex: 50;
  }
  ${mediaQueryUp.lg} {
    height: 100%;
  }
`

// Override the style of react-select elements
const asyncSelectStyle: React.ComponentProps<typeof SelectPlacesAndProperties>['styles'] = {
  input: (provided) => ({
    ...provided,
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    paddingLeft: rem(48),
    maxWidth: 'none',
  }),
}

const customComponents = {
  DropdownIndicator: null,
  ClearIndicator,
  LoadingMessage,
  ValueContainer,
  Input,
  Option,
  Control,
  SingleValue,
}

interface Props {
  setSearchSuggestions?: (searchSuggestions: Array<App.SearchItem>) => void;
  isToggled?: boolean;
  onLocationToggle?: (...args: any) => void;
  onBlur?: () => void;
  recentSearchItems?: Array<App.SearchItem>;
  focusOnMount?: boolean;
  error?: boolean;
  searchTypes?: Array<App.SearchType>;
  placeholder?: string;
  onChange?: (item?: App.SearchItem) => void;
  className?: string;
  titleLabel?: string;
  errorMessage?: string;
  errorTooltipMessage?: string;
  typeaheadTypes?: Array<TypeAheadSearchTypes>;
  /*
    This tells the component to pass it's location data to the secondary location/search item in search  context
    This allows us to have a "to" and "from" in our search context where secondary is often the from
  */
  isSecondaryLocation?: boolean;
  required?: boolean;
  requiredErrorMessage?: string;
  /** Provide your own popular places instead of using searches */
  popularPlaces?: Array<App.SearchItem>;
}

function formatOptionLabel(option: App.SearchItem) {
  const { mainText } = option.format

  return <>{mainText}</>
}

const SearchLocationInput = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    setSearchSuggestions,
    isToggled,
    onLocationToggle,
    onBlur,
    recentSearchItems,
    error,
    searchTypes,
    placeholder = LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL,
    onChange,
    className,
    titleLabel = LOCATION_SEARCH_INPUT_TITLE_LABEL,
    errorMessage = LOCATION_SEARCH_ERROR_MESSAGE,
    errorTooltipMessage = LOCATION_SEARCH_ERROR_TOOLTIP_MESSAGE,
    typeaheadTypes,
    isSecondaryLocation,
    required,
    requiredErrorMessage,
    popularPlaces,
  } = props

  const [localError, setError] = useState<string>()
  const [resultCount, setResultCount] = useState<number>()
  const [searchSuggestionSessionId, setSearchSuggestionSessionId] = useState<string>()
  const [currentSuggestions, setCurrentSuggestions] = useState<Array<App.SearchItem>>([])
  const { currentRegionCode } = useContext(GeoContext)
  const searchState = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const selectRef = useRef<HTMLInputElement>()
  const location = isSecondaryLocation ? searchState.secondaryLocation : searchState.location
  const searchItem = isSecondaryLocation ? searchState.secondarySearchItem : searchState.searchItem
  const searchVerticals = searchState.searchVerticals

  const setSearchItem = useCallback((searchItem?: App.SearchItem) => {
    if (isSecondaryLocation) {
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_SECONDARY_SEARCH_ITEM, searchItem })
    } else {
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_ITEM, searchItem })
    }
  }, [globalSearchDispatch, isSecondaryLocation])

  const setLocation = useCallback((location?: string) => {
    if (isSecondaryLocation) {
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_SECONDARY_LOCATION, location })
    } else {
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_LOCATION, location })
    }
  }, [globalSearchDispatch, isSecondaryLocation])

  const setValue = useCallback((searchItem?: App.SearchItem) => {
    setSearchItem(searchItem)
    if (searchItem) {
      setLocation(searchItem.format.mainText)
      // close the menu if we selected a value
      selectRef.current.blur()
    }
    onChange?.(searchItem)
  }, [onChange, setLocation, setSearchItem])

  const clearValue = useCallback(() => {
    setSearchItem(null)
    setLocation('')
    onChange?.(null)
  }, [onChange, setLocation, setSearchItem])

  const handlePredictionLoad = useCallback(function(options: any, search: string) {
    setResultCount(options.length)
    setSearchSuggestions(options)
    setCurrentSuggestions(options)

    const newSearchSuggestionSessionId = uuidV4()
    setSearchSuggestionSessionId(newSearchSuggestionSessionId)
    trackAutosuggestionsResult({
      region: currentRegionCode,
      verticals: searchVerticals,
      searchSuggestionSessionId: newSearchSuggestionSessionId,
      searchPhrase: search,
      suggestions: options as Array<App.SearchItem>,
    })
  }, [currentRegionCode, setSearchSuggestions, searchVerticals])

  const onLocationChange = useCallback(function(
    changedSearchItem: App.SearchItem,
    { action }: { action: string },
  ) {
    if (action === 'clear') {
      setLocation('')
      setValue(null)
      setSearchSuggestions([])
      return
    } else if (action === 'select-option') {
      setLocation('')
      setValue(changedSearchItem)

      const clickedSuggestionIndex = currentSuggestions.findIndex(item => item.value === changedSearchItem.value)
      trackAutosuggestionClick({
        region: currentRegionCode,
        verticals: searchVerticals,
        searchSuggestionSessionId,
        searchPhrase: location,
        clickedSuggestionIndex,
        clickedSuggestion: changedSearchItem,
      })
    }
  }, [setLocation, setValue, setSearchSuggestions, currentSuggestions, currentRegionCode, searchVerticals, searchSuggestionSessionId, location])

  function onInputChange(newInput: string, { action }: { action: string }) {
    if (action === 'input-change') {
      setValue(undefined)
      setLocation(newInput)
    }
  }

  useImperativeHandle(ref, () => selectRef.current)
  const hasError = !!(localError || error)

  return (
    <Container
      data-testid="search-location-input"
      className={classnames(className, {
        'has-error': hasError,
      })}
    >
      {!!required && <HiddenInput
        value={searchItem ? 'filled' : undefined}
        required
        invalidMessage={requiredErrorMessage}
        onErrorUpdate={setError}
      />}
      <StyledLocationInput
        isClearable
        menuIsOpen={isToggled}
        value={searchItem}
        hasError={hasError}
        onChange={onLocationChange}
        onInputChange={onInputChange}
        formatOptionLabel={formatOptionLabel}
        customComponents={customComponents}
        styles={asyncSelectStyle}
        onFocus={onLocationToggle}
        onPredictionLoad={handlePredictionLoad}
        onBlur={onBlur}
        setValue={setValue}
        ref={selectRef}
        searchTypes={searchTypes}
        typeaheadTypes={typeaheadTypes}
        placeholder={placeholder}
        location={location}
        noOptionsMessage={() => {
          return <NoOptionsMessage
            setValue={setValue}
            recentSearchItems={recentSearchItems}
            resultCount={resultCount}
            message={errorMessage}
            isSecondaryLocation={isSecondaryLocation}
            popularPlaces={popularPlaces}
          />
        }}
        titleLabel={titleLabel}
        clearValue={clearValue}
      />

      <AutoTooltipContent
        open={error}
        position="above-left"
        alignment="left"
        type="error"
      >
        {errorTooltipMessage}
      </AutoTooltipContent>
    </Container>
  )
})

SearchLocationInput.defaultProps = {
  setSearchSuggestions: noop,
  onLocationToggle: noop,
}

SearchLocationInput.displayName = 'SearchLocationInput'

export default SearchLocationInput
